import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { LucideLoader2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import { SignatureInput } from "@/components/SignatureInput";

export const SignatureSettings = () => {
  const { getToken } = useAuth();
  const [value, setValue] = useState(null);   // current/edited signature data URI
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/user/signature`, {
          headers: { Authorization: `Bearer ${token}` }, signal: ac.signal,
        });
        if (res.ok) {
          const json = await res.json();
          setValue(json.signatureBase64 ?? null);
          setOriginal(json.signatureBase64 ?? null);
        }
      } catch (err) {
        if (err.name !== "AbortError") toast.error("Could not load your signature.");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/user/signature`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ signatureBase64: next }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save signature");
      }
      setOriginal(next);
      setValue(next);
      toast.success(next ? "Signature saved." : "Signature removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container my-6 text-muted-foreground">Loading…</div>;

  const dirty = value !== original;

  return (
    <div className="container my-6 max-w-xl">
      <PageHeader
        title="My Signature"
        subtitle="Set up your signature once. You can then sign any of your reports with one click, and it will be stamped onto the report card PDF."
      />

      <div className="rounded-xl border border-border bg-card p-5">
        <SignatureInput value={value} onChange={setValue} />

        <div className="mt-5 flex items-center gap-3">
          <Button onClick={() => save(value)} disabled={saving || !dirty || !value}>
            {saving && <LucideLoader2 className="mr-2 animate-spin" size={16} />}
            Save signature
          </Button>
          {original && (
            <Button variant="outline" onClick={() => save(null)} disabled={saving}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
